/**
 * The event rail: every visible event in order, with a connector line so it
 * reads as one run rather than a stack of cards. Each row carries the time
 * into the session, the type, the step and a one-line preview of what was
 * said, seen or decided — enough to find the moment without clicking it.
 *
 * During playback the selected row is scrolled into view, and rows the clock
 * has not reached yet are dimmed.
 */
import { useEffect, useRef } from 'react';
import type { ReplayEvent } from '../../api/replay';
import { cn } from '@/lib/utils';
import { EVENT_STYLES, toneClass } from './eventStyles';
import { formatOffset } from './time';

interface Props {
  events: ReplayEvent[];
  selectedEventId: string | null;
  onSelectEvent: (id: string) => void;
  sessionStartIso: string;
  /** Playhead, ms since the first visible event: rows past it are dimmed. */
  cursorMs?: number;
}

/** One line of what the event actually contained. */
export function summarize(event: ReplayEvent): string | null {
  switch (event.type) {
    case 'session.started':
      return 'The technician began the procedure.';
    case 'step.started':
      return null;
    case 'step.photo_captured':
      return 'Verification photo captured.';
    case 'step.verified':
      return event.payload.verdictText || `Verdict: ${event.payload.verdict}.`;
    case 'step.retry':
      return event.payload.reason || 'Retake requested.';
    case 'worker_dialogue':
      return `“${event.payload.transcript}”`;
    case 'safety_alert':
      return event.payload.summary;
    case 'session.ended':
      return 'The session closed.';
  }
}

export function ReplayEventList({
  events,
  selectedEventId,
  onSelectEvent,
  sessionStartIso,
  cursorMs,
}: Props) {
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // Guarded: jsdom has no scrollIntoView, and neither do some older
    // embedded browsers.
    selectedRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedEventId]);

  if (events.length === 0) {
    return (
      <div className="px-4 py-10 text-center text-meta text-ink-faint">
        No events match the current filters.
      </div>
    );
  }

  const start = Date.parse(sessionStartIso);

  return (
    <ul data-testid="replay-event-list" className="relative py-1">
      {/* The connector, behind the dots. */}
      <span className="absolute top-3 bottom-3 left-[22px] w-px bg-line" aria-hidden />
      {events.map((event) => {
        const style = EVENT_STYLES[event.type];
        const selected = event.id === selectedEventId;
        const at = Math.max(0, Date.parse(event.timestamp) - start);
        const reached = cursorMs === undefined || at <= cursorMs + 1;
        const preview = summarize(event);
        return (
          <li key={event.id}>
            <button
              type="button"
              ref={selected ? selectedRef : undefined}
              data-testid={`replay-event-row-${event.id}`}
              onClick={() => onSelectEvent(event.id)}
              className={cn(
                'flex w-full gap-3 border-l-2 py-2 pr-3 pl-3 text-left transition-colors',
                toneClass(event.type),
                selected
                  ? 'border-l-ev bg-muted'
                  : 'border-l-transparent hover:bg-muted/60',
                reached ? '' : 'opacity-45',
              )}
            >
              <span className="relative z-10 mt-0.5 grid size-4 shrink-0 place-items-center rounded-full bg-card">
                <span className="size-2.5 rounded-full bg-ev" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-2">
                  <span
                    className={cn('text-meta text-ink', selected ? 'font-semibold' : 'font-medium')}
                  >
                    {style.label}
                  </span>
                  {event.stepNumber !== null ? (
                    <span className="text-meta text-ink-faint">Step {event.stepNumber}</span>
                  ) : null}
                  <span className="ml-auto shrink-0 text-[11px] text-ink-faint tabular-nums">
                    {formatOffset(at)}
                  </span>
                </span>
                {preview ? (
                  <span className="mt-0.5 line-clamp-2 block text-meta text-ink-dim">{preview}</span>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
